import { useCallback, useEffect, useState } from "react";

const API_BASE = process.env.REACT_APP_API_BASE || "";

function inputClass(extra = "") {
  return `w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 ${extra}`;
}

export default function AccountSecuritySection({
  role,
  accountId,
  theme = "pink",
  standalone = false,
}) {
  const accent =
    theme === "blue"
      ? {
          ring: "focus:ring-blue-100 focus:border-blue-400",
          btn: "bg-blue-500 hover:bg-blue-600",
          badge: "bg-blue-50 text-blue-700",
        }
      : {
          ring: "focus:ring-pink-100 focus:border-pink-400",
          btn: "bg-pink-500 hover:bg-pink-600",
          badge: "bg-pink-50 text-pink-700",
        };

  const [statusLoading, setStatusLoading] = useState(true);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailMasked, setEmailMasked] = useState("");
  const [boundEmail, setBoundEmail] = useState("");

  const [bindEmail, setBindEmail] = useState("");
  const [bindCode, setBindCode] = useState("");
  const [bindSending, setBindSending] = useState(false);
  const [bindLoading, setBindLoading] = useState(false);

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changeCode, setChangeCode] = useState("");
  const [changeMode, setChangeMode] = useState("password");
  const [changeSending, setChangeSending] = useState(false);
  const [changeLoading, setChangeLoading] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!accountId) return;
    setStatusLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/auth/email-status?role=${role}&account_id=${accountId}`
      );
      const data = await res.json();
      if (res.ok) {
        setEmailVerified(Boolean(data.email_verified));
        setEmailMasked(data.email_masked || "");
        setBoundEmail(data.email || "");
        if (data.email) setBindEmail(data.email);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setStatusLoading(false);
    }
  }, [accountId, role]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const sendBindCode = async () => {
    if (!bindEmail.trim()) {
      alert("请输入邮箱");
      return;
    }
    setBindSending(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/send-email-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          role,
          account_id: accountId,
          email: bindEmail.trim(),
          purpose: "bind_email",
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "发送失败");
        return;
      }
      alert(
        data.dev_hint
          ? `${data.message}\n（开发模式：请查看后端控制台验证码）`
          : `${data.message}：${data.email_masked}`
      );
    } catch {
      alert("请求失败");
    } finally {
      setBindSending(false);
    }
  };

  const handleBindEmail = async () => {
    if (!bindEmail.trim() || !bindCode.trim()) {
      alert("请填写邮箱和验证码");
      return;
    }
    setBindLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/bind-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          role,
          account_id: accountId,
          email: bindEmail.trim(),
          code: bindCode.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "绑定失败");
        return;
      }
      alert("邮箱绑定成功");
      setBindCode("");
      loadStatus();
    } catch {
      alert("请求失败");
    } finally {
      setBindLoading(false);
    }
  };

  const sendChangeCode = async () => {
    if (!boundEmail) {
      alert("请先绑定邮箱");
      return;
    }
    setChangeSending(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/send-email-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          role,
          account_id: accountId,
          email: boundEmail,
          purpose: "change_password",
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "发送失败");
        return;
      }
      alert(
        data.dev_hint
          ? `${data.message}\n（开发模式：请查看后端控制台验证码）`
          : `${data.message}：${data.email_masked}`
      );
    } catch {
      alert("请求失败");
    } finally {
      setChangeSending(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert("新密码至少 6 位");
      return;
    }
    if (newPassword !== confirmPassword) {
      alert("两次输入的新密码不一致");
      return;
    }

    setChangeLoading(true);
    try {
      const payload = {
        role,
        account_id: accountId,
        new_password: newPassword,
      };
      if (changeMode === "password") {
        if (!oldPassword) {
          alert("请输入原密码");
          setChangeLoading(false);
          return;
        }
        payload.old_password = oldPassword;
      } else {
        if (!changeCode.trim()) {
          alert("请输入邮箱验证码");
          setChangeLoading(false);
          return;
        }
        payload.code = changeCode.trim();
        payload.email = boundEmail;
      }

      const res = await fetch(`${API_BASE}/api/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "修改失败");
        return;
      }
      alert("密码修改成功");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setChangeCode("");
    } catch {
      alert("请求失败");
    } finally {
      setChangeLoading(false);
    }
  };

  return (
    <div
      className={`rounded-2xl border border-gray-100 bg-gray-50/60 p-5 space-y-6 ${
        standalone ? "" : "mt-8"
      }`}
    >
      {!standalone && (
        <div>
          <h2 className="text-lg font-bold text-gray-800">账号安全</h2>
          <p className="text-sm text-gray-500 mt-1">
            绑定邮箱后，可通过邮箱验证码修改或找回密码。
          </p>
        </div>
      )}

      <div className="rounded-xl bg-white p-4 border border-gray-100 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm font-medium text-gray-700">邮箱绑定</p>
          {statusLoading ? (
            <span className="text-xs text-gray-400">加载中…</span>
          ) : emailVerified ? (
            <span className={`text-xs px-2 py-1 rounded-full ${accent.badge}`}>
              已绑定 {emailMasked}
            </span>
          ) : (
            <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700">
              未绑定
            </span>
          )}
        </div>

        <input
          type="email"
          value={bindEmail}
          onChange={(e) => setBindEmail(e.target.value)}
          placeholder="请输入常用邮箱"
          disabled={emailVerified}
          className={inputClass(emailVerified ? "bg-gray-50" : "")}
        />

        {!emailVerified && (
          <>
            <div className="flex gap-2">
              <input
                value={bindCode}
                onChange={(e) => setBindCode(e.target.value)}
                placeholder="邮箱验证码"
                className={inputClass("flex-1")}
              />
              <button
                type="button"
                onClick={sendBindCode}
                disabled={bindSending}
                className={`shrink-0 px-4 py-3 rounded-xl text-sm text-white ${accent.btn} disabled:opacity-50`}
              >
                {bindSending ? "发送中…" : "获取验证码"}
              </button>
            </div>
            <button
              type="button"
              onClick={handleBindEmail}
              disabled={bindLoading}
              className={`w-full py-3 rounded-xl text-sm text-white ${accent.btn} disabled:opacity-50`}
            >
              {bindLoading ? "绑定中…" : "确认绑定邮箱"}
            </button>
          </>
        )}
      </div>

      <div className="rounded-xl bg-white p-4 border border-gray-100 space-y-3">
        <p className="text-sm font-medium text-gray-700">修改密码</p>

        <div className="flex gap-2 text-sm">
          <button
            type="button"
            onClick={() => setChangeMode("password")}
            className={`px-3 py-1.5 rounded-lg ${
              changeMode === "password"
                ? "bg-gray-800 text-white"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            原密码
          </button>
          <button
            type="button"
            onClick={() => setChangeMode("email")}
            disabled={!emailVerified}
            className={`px-3 py-1.5 rounded-lg ${
              changeMode === "email"
                ? "bg-gray-800 text-white"
                : "bg-gray-100 text-gray-600"
            } disabled:opacity-40`}
          >
            邮箱验证码
          </button>
        </div>

        {changeMode === "password" ? (
          <input
            type="password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            placeholder="原密码"
            className={inputClass()}
          />
        ) : (
          <div className="flex gap-2">
            <input
              value={changeCode}
              onChange={(e) => setChangeCode(e.target.value)}
              placeholder="邮箱验证码"
              className={inputClass("flex-1")}
            />
            <button
              type="button"
              onClick={sendChangeCode}
              disabled={changeSending || !emailVerified}
              className={`shrink-0 px-4 py-3 rounded-xl text-sm text-white ${accent.btn} disabled:opacity-50`}
            >
              {changeSending ? "发送中…" : "获取验证码"}
            </button>
          </div>
        )}

        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="新密码（至少 6 位）"
          className={inputClass()}
        />
        <input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="确认新密码"
          className={inputClass()}
        />
        <button
          type="button"
          onClick={handleChangePassword}
          disabled={changeLoading}
          className={`w-full py-3 rounded-xl text-sm text-white ${accent.btn} disabled:opacity-50`}
        >
          {changeLoading ? "提交中…" : "保存新密码"}
        </button>
      </div>
    </div>
  );
}
